import React, { createRef, Fragment } from 'react'
import classNames from 'classnames'

import type { MenuType } from 'types'

import Link from 'content-components/_Primatives/Link'
import Menu from '../Menu'
import MenuItem from '../MenuItem'

import style from './style.scss'

type Props = {
  'aria-label'?: string
  className?: string
  links: MenuType[]
}

type State = {
  activeMenu: number
}

class BrandBar extends React.PureComponent<Props, State> {
  static defaultProps = {
    className: '',
  }

  state = {
    activeMenu: -1,
  }

  wrapperRef: { current: HTMLElement | null } = createRef()

  componentDidMount() {
    window.addEventListener('touchend', this.handleEvents)
  }

  componentWillUnmount() {
    window.removeEventListener('touchend', this.handleEvents)
  }

  handleEvents = (event: TouchEvent) => {
    const el = event.target
    const wrapper = this.wrapperRef.current

    if (wrapper && el instanceof Node && !wrapper.contains(el)) {
      this.setState({ activeMenu: -1 })
    }
  }

  updateActiveMenu = (activeMenu: number) => {
    this.setState({ activeMenu })
  }

  updateActiveMenuByTouch = (index: number) => {
    this.setState(({ activeMenu }) => ({
      activeMenu: activeMenu === index ? -1 : index,
    }))
  }

  renderMenuItem = (links: Array<MenuType>): React.ReactNode =>
    links.map(({ text, url, target, below }: MenuType, index: number) => {
      const { activeMenu } = this.state

      return (
        <li
          key={text}
          className={style.brandBarItem}
          onFocus={() => {
            this.updateActiveMenu(index)
          }}
          onMouseEnter={() => this.updateActiveMenu(index)}
          onMouseLeave={() => this.updateActiveMenu(-1)}
        >
          {below ? (
            <>
              <button
                className={classNames(
                  style.brandBarButton,
                  { [style.expanded]: activeMenu === index },
                  { [style.brandBarButtonWithBelow]: !!below },
                )}
                onTouchStart={() => this.updateActiveMenuByTouch(index)}
              >
                {text}
              </button>
              {this.renderSubMenu(below, index)}
            </>
          ) : (
            <Link
              className={classNames(style.brandBarButton, {
                [style.expanded]: activeMenu === index,
              })}
              target={target}
              url={url}
            >
              {text}
            </Link>
          )}
        </li>
      )
    })

  renderSubMenu = (links: Array<MenuType>, index: number) => {
    const { activeMenu } = this.state

    return (
      <ul
        className={classNames(style.subMenu, {
          [style.expanded]: activeMenu === index,
        })}
      >
        {links.map(this.renderSubMenuItem)}
      </ul>
    )
  }

  renderSubMenuItem = (link: MenuType) => {
    const { below, text } = link
    return (
      <Fragment key={text}>
        <MenuItem className={style.brandBarBelowItem} {...link} />
        {below &&
          below.map((subLink: MenuType) => {
            return (
              <MenuItem
                key={subLink.text}
                className={style.brandBarBelowSubItem}
                {...subLink}
              />
            )
          })}
      </Fragment>
    )
  }

  render() {
    const { 'aria-label': ariaLabel, className, links } = this.props
    const { activeMenu } = this.state

    return (
      <Menu
        aria-label={ariaLabel}
        className={classNames(style.wrapper, className, {
          [style.active]: activeMenu > -1,
        })}
        inputRef={this.wrapperRef}
      >
        {this.renderMenuItem(links)}
      </Menu>
    )
  }
}

export default BrandBar
